/**
 * Raw gantt link → engine `LinkSnapshot`.
 *
 * Used for links that are not part of a snapshot (yet): the candidate an
 * `onBeforeLinkAdd` / `onBeforeLinkUpdate` handler receives, and the argument
 * of a `gantt.isCircularLink(link)` call (GS-3553). Lives on its own so the
 * public-API wrapper in `ext/auto_scheduling.js` does not have to pull in the
 * whole trigger module for it.
 *
 * Adapter-level, but gantt-free (D-33): the caller passes the raw link object
 * and the configured duration unit.
 */

import type { LinkSnapshot, LinkType, RawLinkType, TaskID, TimeUnit } from "../core/types";
import { normalizeLinkId, normalizeTaskId } from "../core/ids";

/** The subset of a gantt link this module reads. */
export interface LinkRecord {
  id: TaskID;
  source: TaskID;
  target: TaskID;
  type?: RawLinkType;
  lag?: number;
}

export function normalizeLinkType(raw: RawLinkType | undefined): LinkType {
  const s = String(raw ?? "0");
  switch (s) {
    case "0": case "fs": return "fs";
    case "1": case "ss": return "ss";
    case "2": case "ff": return "ff";
    case "3": case "sf": return "sf";
    default: return "fs";
  }
}

export function buildCandidateLinkSnapshot(link: LinkRecord, durationUnit: TimeUnit): LinkSnapshot {
  return {
    id: normalizeLinkId(link.id),
    rawId: link.id,
    source: normalizeTaskId(link.source),
    rawSource: link.source,
    target: normalizeTaskId(link.target),
    rawTarget: link.target,
    type: normalizeLinkType(link.type),
    lag: typeof link.lag === "number" ? link.lag : 0,
    lagUnit: durationUnit
  };
}
